import {PlayerViewType} from "../player/models/PlayerTypes";
import {VisualParameters} from "../player/models/parameters/VisualParameters";
import {masteriesService} from "./MasteriesService";
import {userService} from "../user/UserService";

export function getMasterType(playerView: PlayerViewType, visualParameters: VisualParameters) {
  const masterTypeMode = (playerView === 'piano')
    ? 'RHYTHM'
    : 'CHORD';

  const masterTypeHand = (visualParameters.isTimelineContentsDisplayed.LEFT && visualParameters.isTimelineContentsDisplayed.RIGHT)
    ? 'ME'
    : visualParameters.isTimelineContentsDisplayed.LEFT ? 'LEFT'
      : 'RIGHT';

  const masterType = masterTypeHand + '_' + masterTypeMode;

  if (masterType !== 'ME_CHORD') {
    return masterType;
  }
  else {
    return null;
  }
}

export function getScoreDifferenceMap(newScores: any[], oldScores: any[], minDiff: number) {
  return newScores.map((newScoreEntry: any) => {
    const oldEntry: any = oldScores.find((oldScore: any) => {
      return newScoreEntry.id === oldScore.id
      // && oldScore.piano_class_progress < 1 && !oldScore.completed_at // Was used to remove completed entry, but before we returned 0 if old Entry wasn't found
      // Now do it in filter function and for now keep class not hidden since we don't display progress in it
    });
    newScoreEntry.score_diff = (oldEntry) ? newScoreEntry.score - oldEntry.score : newScoreEntry.score
    newScoreEntry.old_score = oldEntry?.score ?? null
    return newScoreEntry
  }).filter((changeScore: any) =>{
    return changeScore.score_diff > minDiff && !changeScore.completed_at
  });
}

export function addScoreDiffToPlayableList(playableList: any[]) {
  return playableList.map((entry) => {
    if (entry.piece_version_id) {
      entry.song_diff = masteriesService.sessionScoreDiff.challenges.find((scoreDiff) => scoreDiff.id === entry.id)?.score_diff ?? 0
    }
    else {
      entry.song_diff = masteriesService.sessionScoreDiff.versions.find((scoreDiff) => scoreDiff.id === entry.id)?.score_diff ?? 0
    }
    return entry
  })
}


/* Teacher Score Description */

type userDifficulty = 'EASY' | 'MATCHING' | 'HARD' | 'REALLY_HARD'
function getDifficultyForUser(difficulty: number): userDifficulty {
  const userCatalogDifficulty = userService.user.level.catalog_difficulty;
  if (userCatalogDifficulty > difficulty) {
    return 'EASY';
  }
  else if (userCatalogDifficulty === difficulty) {
    return 'MATCHING';
  }
  else if (userCatalogDifficulty + 1 === difficulty) {
    return 'HARD';
  }
  else {
    return 'REALLY_HARD';
  }
}

const DIFFICULTY_ADVICES = {
  EASY: [
    `<span class="text-bold text-green">Vous devriez travailler ce __contentType__ pour consolider vos bases en piano&nbsp;!</span> Il contient beaucoup de techniques que nous n’avons pas vu ensemble et il sera <b>facile</b> à apprendre ! car d'un niveau inférieur`,
    `<span class="text-bold text-green">Ce __contentType__ est bien pour consolider vos bases en piano&nbsp;!</span> Vous en maîtrisez peu de techniques mais il sera tout de même <b>facile</b> à apprendre ! compte tenu de votre niveau.`,
    `<span class="text-bold text-green">Vous devriez travailler ce __contentType__ pour consolider vos bases</span> car vous maîtrisez déjà la moitié des techniques nécessaires pour le jouer. Compte tenu de votre niveau, il sera <b>facile</b> à apprendre !`,
    `<span class="text-bold text-green">Si ce __contentType__ vous plaît, je vous recommande de le travailler.</span> Compte tenu de votre niveau et des techniques que vous maîtrisez, il sera <b>facile</b> à apprendre !`,
    `<span class="text-bold text-green">Ce __contentType__ est bien à travailler&nbsp;!</span> Vous maîtrisez presque toutes les techniques pour le jouer et son niveau de difficulté est très abordable pour vous, il sera <b>facile</b> à apprendre !`,
  ],
  MATCHING: [
    `<span class="text-bold text-primary">Travailler ce __contentType__ est un bon défi à relever&nbsp;!</span> Il est <b>adapté à votre niveau</b> mais il contient presque uniquement des techniques que vous ne maitrisez pas encore. Il sera peut-être <b>un peu difficile</b> à apprendre.`,
    `<span class="text-bold text-primary">Travailler ce __contentType__ est un bon défi à relever&nbsp;!</span> Il est <b>adapté à votre niveau</b> et contient beaucoup de nouvelles techniques à connaître. Il sera <b>plutôt facile</b> à apprendre et vous allez progresser.`,
    `<span class="text-bold text-primary">C'est une bonne idée de travailler ce __contentType__&nbsp;!</span> Vous maîtrisez presque la moitié des techniques pour le jouer et il est <b>adapté à votre niveau</b>. Il vous permettra de progresser tout en étant <b>plutôt facile</b> à apprendre !`,
    `<span class="text-bold text-green">Je vous recommande de travailler ce __contentType__&nbsp;!</span> Il sera <b>facile à apprendre</b> car vous maîtrisez plus de la moitié des techniques pour le jouer et il est <b>adapté à votre niveau.</b>`,
    `<span class="text-bold text-green">Ce __contentType__ me semble prioritaire à travailler&nbsp;!</span> Vous maîtrisez presque toutes les techniques pour le jouer et il est <b>adapté à votre niveau</b>. Il sera <b>facile</b> à apprendre.`,
  ],
  HARD: [
    `<span class="text-bold text-red">Je ne vous recommande pas de travailler ce __contentType__ pour le moment.</span> Vous ne maîtrisez presque aucune des techniques nécessaires pour le jouer et il est <b>inadapté à votre niveau</b>. Nous y viendrons plus tard !`,
    `<span class="text-bold text-warning">Travailler ce __contentType__ est peut être un trop gros défi&nbsp;!</span> D'un <b>niveau supérieur</b>, il contient beaucoup de techniques que vous ne maitrisez pas encore et risque d'être <n>difficile</b> à apprendre.`,
    `<span class="text-bold text-warning">Apprendre ce __contentType__ sera un gros défi&nbsp;!</span> Vous maîtrisez presque la moitié des techniques pour le jouer mais il est d’un <b>niveau supérieur</bb/>. Il sera <b>difficile</b> à apprendre.`,
    `<span class="text-bold text-warning">Apprendre ce __contentType__ est un bon défi si vous en cherchez un&nbsp;!</span> Vous maîtrisez plus de la moitié des techniques pour le jouer mais il est d'un <b>niveau supérieur</b> et ne sera <b>pas facile</b> à apprendre !`,
    `<span class="text-bold text-primary">Travailler ce __contentType__ est une bonne idée&nbsp;!</span> Il ne sera <b>pas facile</b> de l'apprendre car il est d'un <b>niveau supérieur</b> mais vous maitrisez presque toutes les techniques pour le jouer ! `,
  ],
  REALLY_HARD: [
    `<span class="text-bold text-red">Je pense que ce __contentType__ est trop difficile pour vous</span> mais le but est de vous faire progresser pour vous permettre de pouvoir le jouer plus tard !`,
    `<span class="text-bold text-red">Je ne vous recommande pas d'apprendre ce __contentType__ pour le moment</span> car il est <b>trop difficile</b> ! Mais vous êtes sur la bonne voie.`,
    `<span class="text-bold text-red">Je vous déconseille de travailler ce __contentType__ tout de suite</span> car il me semble encore <b>trop difficile</b> pour vous même si vous maîtrisez presque la moitié des techniques pour le jouer. Bientôt ! `,
    `<span class="text-bold text-red">Travailler ce __contentType__ me semble être un trop gros défi&nbsp;!</span> Vous maîtrisez plus de la moitié des techniques pour le jouer mais le reste va être <b>très difficile</b> à apprendre.`,
    `<span class="text-bold text-warning">Travailler ce __contentType__ me semble ambitieux mais il peut être un bon défi à relever pour progresser&nbsp;!</span> Même si vous maîtrisez presque toutes les techniques pour le jouer, il va être <b>très difficile</b> à apprendre.`,
  ],
  NOT_INTERESTING: [
    `Vous avez indiqué ne pas être <span class='text-bold text-red'>intéressé par l'apprentissage de ce __contentType__</span>. Cependant, vous pouvez toujours décider de le travailler ultérieurement en cliquant sur "jouer".`
  ]
}

export function getScoreDescription(score: number, difficulty: number, notInteresting = false, contentType: string = 'morceau', progress: number = 0) {
  
  if (progress === 100) {
    return 'Bravo ! Vous avez terminé ce __contentType__, vous pouvez le rejouer pour le réviser et renforcer vos compétences ou tout simplement pour vous faire plaisir !'
      .replace(/__contentType__/g, contentType);
  }
  else if(notInteresting) {
    return DIFFICULTY_ADVICES.NOT_INTERESTING[0].replace(/__contentType__/g, contentType);
  }
  else if (progress > 0 && progress < 100 && contentType === 'morceau') {
    return 'Vous avez déjà fait ' + Math.round(progress) + '% du cours de ce morceau. Continuez à l\'apprendre !'
  }
  const difficultyForUser = getDifficultyForUser(difficulty);
  if (!score || score < 20) {
    return DIFFICULTY_ADVICES[difficultyForUser][0].replace(/__contentType__/g, contentType);
  }
  else if (score < 35) {
    return DIFFICULTY_ADVICES[difficultyForUser][1].replace(/__contentType__/g, contentType);
  }
  else if (score < 50) {
    return DIFFICULTY_ADVICES[difficultyForUser][2].replace(/__contentType__/g, contentType);
  }
  else if (score < 75) {
    return DIFFICULTY_ADVICES[difficultyForUser][3].replace(/__contentType__/g, contentType);
  }
  else {
    return DIFFICULTY_ADVICES[difficultyForUser][4].replace(/__contentType__/g, contentType);
  }
}


